import { useLocation, Link } from "react-router-dom";
import { ChevronRight, HomeIcon } from "lucide-react";

import { cn } from "@utils/utils";
import { Beta } from "../Beta";
import { useBreadcrumbContext } from "@shared/contexts";

interface BreadcrumbProps {
  className?: string;
  alertTitle?: string;
}

const routeLabels: Record<string, { label: string; path: string }> = {
  "alert-details": { label: "Detalles de alerta", path: "" },
  communication: { label: "Comunicación", path: "/communication" },
  notifications: { label: "Notificaciones", path: "/notifications" },
  "condiciones-meteorologicas": {
    label: "Condiciones Meteorológicas",
    path: "/observatory/condiciones-meteorologicas",
  },
  "create-forecast": {
    label: "Crear pronóstico",
    path: "/communication/create-forecast",
  },
  "create-multiple-forecast": {
    label: "Crear pronóstico múltiple",
    path: "/communication/create-multiple-forecast",
  },
  "create-user": { label: "Crear usuario", path: "/users/create-user" },
  profiles: { label: "Perfiles", path: "/users/profiles" },
  drill: {
    label: "Simulacros",
    path: "/communication/list-alerts-phenomenon/drill",
  },
  "eventos-tormenta": {
    label: "Eventos de Lluvia",
    path: "/observatory/eventos-tormenta",
  },
  flood: {
    label: "Inundación",
    path: "/communication/list-alerts-phenomenon/flood",
  },
  forecast: {
    label: "Pronóstico",
    path: "/communication/list-alerts-pronostics/forecast",
  },
  home: { label: "Home", path: "/home" },
  "list-alerts": {
    label: "Lista de alertas",
    path: "/communication/list-alerts",
  },
  monitor: { label: "Monitor", path: "/monitor" },
  info: { label: "Notificaciones informativas", path: "/communication/info" },
  general: {
    label: "Informativas Generales",
    path: "/communication/list-alerts-info/general",
  },
  traffic: {
    label: "Tráfico",
    path: "/communication/list-alerts-info/traffic",
  },
  security: {
    label: "Seguridad",
    path: "/communication/list-alerts-info/security",
  },
  news: {
    label: "Notificaciones de noticias",
    path: "/communication/list-alerts-news",
  },
  observatory: { label: "Observatorio", path: "/observatory" },
  phenomenon: { label: "Fenómeno", path: "/communication/phenomenon" },
  report: { label: "Reportes", path: "/report" },
  storm: {
    label: "Lluvia",
    path: "/communication/list-alerts-phenomenon/storm",
  },
  "list-alerts-phenomenon": {
    label: "Historico fenómenos",
    path: "/communication/list-alerts-phenomenon",
  },
  "list-alerts-info": {
    label: "Histórico informativas",
    path: "/communication/list-alerts-info",
  },
  "list-alerts-pronostics": {
    label: "Histórico pronósticos",
    path: "/communication/list-alerts-pronostics",
  },
  "list-alerts-news": {
    label: "Histórico noticias",
    path: "/communication/list-alerts-news",
  },
  temp_flood: {
    label: "Inundación",
    path: "/communication/list-alerts-phenomenon/flood",
  },
  users: { label: "Usuarios", path: "/users" },
  wind: {
    label: "Vientos",
    path: "/communication/list-alerts-phenomenon/wind",
  },
  frost: {
    label: "Heladas",
    path: "/communication/list-alerts-phenomenon/frost",
  },
  tropical_cyclone: {
    label: "Ciclones tropicales",
    path: "/communication/list-alerts-phenomenon/tropical-cyclone",
  },
  forest_fire: {
    label: "Incendios forestales",
    path: "/communication/list-alerts-phenomenon/forest_fire",
  },
  volcanic: {
    label: "Actividad volcánica",
    path: "/communication/list-alerts-phenomenon/volcanic",
  },
  low_temperatures: {
    label: "Bajas temperaturas",
    path: "/communication/list-alerts-phenomenon/low_temperatures",
  },
  environmental_risk: {
    label: "Contingencia atmosférica",
    path: "/communication/list-alerts-phenomenon/environmental_risk",
  },
  hail: {
    label: "Granizo",
    path: "/communication/list-alerts-phenomenon/hail",
  },
  dust_devil: {
    label: "Tolvanera",
    path: "/communication/list-alerts-phenomenon/dust_devil",
  },
  fog: {
    label: "Niebla",
    path: "/communication/list-alerts-phenomenon/fog",
  },
  analytics: { label: "Reportes EDL", path: "/analytics" },
  "demographic-graphs": {
    label: "Reportes App Jalisco alerta",
    path: "/analytics/demographic-graphs",
  },
  "mapa-interactivo": {
    label: "Estaciones meteorológicas",
    path: "/observatory/mapa-interactivo",
  },
  "monitoreo-sismico": {
    label: "Monitor Sísmico",
    path: "/observatory/monitoreo-sismico",
  },
  "pluvial-risk-map": {
    label: "Mapa de monitoreo",
    path: "/monitor/pluvial-risk-map",
  },
  "threshold-settings": {
    label: "Configuración de umbrales",
    path: "/map/threshold-settings",
  },
  "high-temperatures": {
    label: "Altas temperaturas",
    path: "/mapthreshold-settings/high-temperatures",
  },
  "low-temperatures": {
    label: "Bajas temperaturas",
    path: "/map/threshold-settings/low-temperatures",
  },
  wildlife: {
    label: "Fauna silvestre",
    path: "/communication/list-alerts-info/wildlife",
  },
  map: { label: "Mapa Interactivo", path: "/map" },
  "threshold-intensity-precipitation": {
    label: "Intensidad de Precipitación",
    path: "/map/threshold-settings/storm",
  },
};

export const Breadcrumb = ({ className, alertTitle }: BreadcrumbProps) => {
  const { hideBeta } = useBreadcrumbContext();
  const location = useLocation();
  const paths = location.pathname.split("/").filter(Boolean);

  if (paths.length === 0) return null;

  const buildPath = (index: number) => {
    return "/" + paths.slice(0, index + 1).join("/");
  };

  return (
    <div
      className={cn(
        "ml-6 bg-white/40 backdrop-blur-md rounded-full p-1 shadow-sm z-10 border border-white/20 w-fit",
        className,
      )}
    >
      <nav
        className="bg-white rounded-full px-4 py-2 inline-block"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center space-x-2 text-sm">
          <li className="text-gray-600">
            <Link
              to="/home"
              className="hover:text-foreground transition-colors flex items-center gap-2"
            >
              <HomeIcon className="w-4 h-4"/>
              Home
            </Link>
          </li>
          {paths.map((path, index) => {
            const isLastPath = index === paths.length - 1;
            const routeInfo = routeLabels[path];

            let label = routeInfo?.label || path;
            let pathTo = routeInfo?.path || buildPath(index);

            if (path === "info") {
              const currentFullPath = buildPath(index);
              if (currentFullPath.includes("/list-alerts-info")) {
                label = "Informativas";
                pathTo = "/communication/list-alerts-info/info";
              }
            }

            const isUUID =
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                path,
              );
            const displayText =
              alertTitle &&
              (path === "alert-details" || isUUID || (isLastPath && !routeInfo))
                ? `Detalle: ${alertTitle}`
                : label;

            return (
              <li key={index} className="flex items-center space-x-2">
                <ChevronRight className="h-4 w-4 text-[#7B8A99]" />
                {isLastPath ? (
                  <span className="flex items-center gap-2 text-foreground font-medium">
                    {displayText}
                    {(path === "create-multiple-forecast" ||
                      path === "pluvial-risk-map" ||
                      (path === "demographic-graphs" && !hideBeta) ||
                      path === "map" ||
                      path === "threshold-settings" ||
                      path === "monitoreo-sismico") && <Beta />}
                  </span>
                ) : (
                  <Link
                    to={pathTo}
                    className="text-gray-600 hover:text-foreground transition-colors"
                  >
                    {displayText}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};
